/**
 * Auth store: the signed-in user (or null) and the auth actions, all against /api (PLAN §5.4).
 */
import { create } from 'zustand';
import type { User } from '@tonist/shared';
import { get, post, isApiError } from '../lib/api';

export type AuthStatus = 'unknown' | 'anonymous' | 'signed-in';

export interface AuthState {
  user: User | null;
  status: AuthStatus;
  refresh(): Promise<User | null>;
  login(email: string, password: string): Promise<User>;
  signup(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  activate(token: string): Promise<User>;
  forgot(email: string): Promise<void>;
  reset(token: string, password: string): Promise<void>;
  magic(email: string): Promise<void>;
  magicConsume(token: string): Promise<User>;
}

/** Accept `{ user }`, `{ user: null }` or a bare user object. */
export function normaliseUser(data: unknown): User | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const u = ('user' in o ? o.user : o) as Record<string, unknown> | null;
  if (!u || typeof u !== 'object' || typeof u.id !== 'string' || typeof u.email !== 'string') return null;
  return { id: u.id, email: u.email, createdAt: typeof u.createdAt === 'number' ? u.createdAt : 0 };
}

export const useAuth = create<AuthState>((set, getState) => ({
  user: null,
  status: 'unknown',
  async refresh() {
    try {
      const user = normaliseUser(await get<unknown>('/me'));
      set({ user, status: user ? 'signed-in' : 'anonymous' });
      return user;
    } catch (e) {
      if (isApiError(e) && e.status === 0) { set({ status: getState().user ? 'signed-in' : 'anonymous' }); return getState().user; }
      set({ user: null, status: 'anonymous' });
      return null;
    }
  },
  async login(email, password) {
    const data = await post<unknown>('/auth/login', { email, password });
    const user = normaliseUser(data) ?? (await getState().refresh());
    if (!user) throw new Error('Signed in, but no user came back');
    set({ user, status: 'signed-in' });
    return user;
  },
  async signup(email, password) { await post('/auth/signup', { email, password }); },
  async logout() {
    try { await post('/auth/logout'); } finally { set({ user: null, status: 'anonymous' }); }
  },
  async activate(token) {
    const data = await post<unknown>('/auth/activate', { token });
    const user = normaliseUser(data) ?? (await getState().refresh());
    if (!user) throw new Error('Activated, but no session came back');
    set({ user, status: 'signed-in' });
    return user;
  },
  async forgot(email) { await post('/auth/password/forgot', { email }); },
  async reset(token, password) { await post('/auth/password/reset', { token, password }); },
  async magic(email) { await post('/auth/magic', { email }); },
  async magicConsume(token) {
    const data = await post<unknown>('/auth/magic/consume', { token });
    const user = normaliseUser(data) ?? (await getState().refresh());
    if (!user) throw new Error('Link accepted, but no session came back');
    set({ user, status: 'signed-in' });
    return user;
  },
}));

export const initials = (email: string): string => (email.split('@')[0] ?? '?').slice(0, 2).toUpperCase();
